// 座席形状タイプ
export type SeatShape = "tate" | "yoko" | "square";

// 座席位置定義（絶対座標: px）- レガシー
export interface SeatPosition {
  seatId: number;
  x: number;
  y: number;
  width: number;
  height: number;
  shape: SeatShape;
  area: string;
}

// 座席位置定義（相対座標: %）
export interface SeatPositionRelative {
  seatId: number;
  xPercent: number;
  yPercent: number;
  widthPercent: number;
  heightPercent: number;
  shape: SeatShape;
  area: string;
}

// デフォルト値（API取得失敗時のフォールバック用）
export const DEFAULT_IMAGE_WIDTH = 1142;
export const DEFAULT_IMAGE_HEIGHT = 1682;
export const DEFAULT_Y_OFFSET = 133;
export const DEFAULT_EFFECTIVE_HEIGHT = DEFAULT_IMAGE_HEIGHT - DEFAULT_Y_OFFSET; // 1549
export const DEFAULT_IMAGE_URL = "/img/office3.png";

// 後方互換性のためのエイリアス（既存コードで使用している箇所向け）
export const ORIGINAL_IMAGE_WIDTH = DEFAULT_IMAGE_WIDTH;
export const ORIGINAL_IMAGE_HEIGHT = DEFAULT_IMAGE_HEIGHT;
export const Y_OFFSET = DEFAULT_Y_OFFSET;
export const EFFECTIVE_HEIGHT = DEFAULT_EFFECTIVE_HEIGHT;

/**
 * 絶対座標(px) → 相対座標(%) 変換
 */
export function toRelativePosition(seat: SeatPosition): SeatPositionRelative {
  return {
    seatId: seat.seatId,
    xPercent: (seat.x / ORIGINAL_IMAGE_WIDTH) * 100,
    yPercent: ((seat.y - Y_OFFSET) / EFFECTIVE_HEIGHT) * 100,
    widthPercent: (seat.width / ORIGINAL_IMAGE_WIDTH) * 100,
    heightPercent: (seat.height / EFFECTIVE_HEIGHT) * 100,
    shape: seat.shape,
    area: seat.area,
  };
}

// PHPコードから抽出した座席位置データ（絶対座標: px）
// 元の座標は画像サイズ1142x1682ベース
export const SEAT_POSITIONS: SeatPosition[] = [
  // 役員席 (0-4) → seatId 1-5
  {
    seatId: 1,
    x: 928,
    y: 826,
    width: 61,
    height: 81,
    shape: "tate",
    area: "役員席",
  },
  {
    seatId: 2,
    x: 928,
    y: 630,
    width: 61,
    height: 81,
    shape: "tate",
    area: "役員席",
  },
  {
    seatId: 3,
    x: 928,
    y: 404,
    width: 61,
    height: 81,
    shape: "tate",
    area: "役員席",
  },
  {
    seatId: 4,
    x: 736,
    y: 330,
    width: 74,
    height: 51,
    shape: "yoko",
    area: "役員席",
  },
  {
    seatId: 5,
    x: 570,
    y: 330,
    width: 74,
    height: 51,
    shape: "yoko",
    area: "役員席",
  },

  // 経理エリア (5-8) → seatId 6-9
  {
    seatId: 6,
    x: 198,
    y: 403,
    width: 74,
    height: 51,
    shape: "yoko",
    area: "経理エリア",
  },
  {
    seatId: 7,
    x: 302,
    y: 403,
    width: 74,
    height: 51,
    shape: "yoko",
    area: "経理エリア",
  },
  {
    seatId: 8,
    x: 198,
    y: 466,
    width: 74,
    height: 51,
    shape: "yoko",
    area: "経理エリア",
  },
  {
    seatId: 9,
    x: 302,
    y: 466,
    width: 74,
    height: 51,
    shape: "yoko",
    area: "経理エリア",
  },

  // 経理エリア下 (9-12) → seatId 10-13
  {
    seatId: 10,
    x: 198,
    y: 666,
    width: 74,
    height: 51,
    shape: "yoko",
    area: "経理エリア下",
  },
  {
    seatId: 11,
    x: 302,
    y: 666,
    width: 74,
    height: 51,
    shape: "yoko",
    area: "経理エリア下",
  },
  {
    seatId: 12,
    x: 198,
    y: 729,
    width: 74,
    height: 51,
    shape: "yoko",
    area: "経理エリア下",
  },
  {
    seatId: 13,
    x: 302,
    y: 729,
    width: 74,
    height: 51,
    shape: "yoko",
    area: "経理エリア下",
  },

  // 中央1 (13-18) → seatId 14-19
  {
    seatId: 14,
    x: 540,
    y: 466,
    width: 61,
    height: 81,
    shape: "tate",
    area: "中央1",
  },
  {
    seatId: 15,
    x: 540,
    y: 570,
    width: 61,
    height: 81,
    shape: "tate",
    area: "中央1",
  },
  {
    seatId: 16,
    x: 540,
    y: 684,
    width: 61,
    height: 81,
    shape: "tate",
    area: "中央1",
  },
  {
    seatId: 17,
    x: 604,
    y: 466,
    width: 61,
    height: 81,
    shape: "tate",
    area: "中央1",
  },
  {
    seatId: 18,
    x: 604,
    y: 570,
    width: 61,
    height: 81,
    shape: "tate",
    area: "中央1",
  },
  {
    seatId: 19,
    x: 604,
    y: 684,
    width: 61,
    height: 81,
    shape: "tate",
    area: "中央1",
  },

  // 中央2 (19-22) → seatId 20-23
  {
    seatId: 20,
    x: 540,
    y: 838,
    width: 61,
    height: 81,
    shape: "tate",
    area: "中央2",
  },
  {
    seatId: 21,
    x: 540,
    y: 938,
    width: 61,
    height: 81,
    shape: "tate",
    area: "中央2",
  },
  {
    seatId: 22,
    x: 604,
    y: 838,
    width: 61,
    height: 81,
    shape: "tate",
    area: "中央2",
  },
  {
    seatId: 23,
    x: 604,
    y: 938,
    width: 61,
    height: 81,
    shape: "tate",
    area: "中央2",
  },

  // 左下 (23-30) → seatId 24-31
  {
    seatId: 24,
    x: 260,
    y: 972,
    width: 61,
    height: 61,
    shape: "square",
    area: "左下",
  },
  {
    seatId: 25,
    x: 325,
    y: 1038,
    width: 61,
    height: 61,
    shape: "square",
    area: "左下",
  },
  {
    seatId: 26,
    x: 392,
    y: 1103,
    width: 61,
    height: 61,
    shape: "square",
    area: "左下",
  },
  {
    seatId: 27,
    x: 455,
    y: 1167,
    width: 61,
    height: 61,
    shape: "square",
    area: "左下",
  },
  {
    seatId: 28,
    x: 204,
    y: 1032,
    width: 61,
    height: 61,
    shape: "square",
    area: "左下",
  },
  {
    seatId: 29,
    x: 266,
    y: 1098,
    width: 61,
    height: 61,
    shape: "square",
    area: "左下",
  },
  {
    seatId: 30,
    x: 335,
    y: 1162,
    width: 61,
    height: 61,
    shape: "square",
    area: "左下",
  },
  {
    seatId: 31,
    x: 398,
    y: 1227,
    width: 61,
    height: 61,
    shape: "square",
    area: "左下",
  },

  // 右下 (31-36) → seatId 32-37
  {
    seatId: 32,
    x: 750,
    y: 1128,
    width: 61,
    height: 61,
    shape: "square",
    area: "右下",
  },
  {
    seatId: 33,
    x: 675,
    y: 1203,
    width: 61,
    height: 61,
    shape: "square",
    area: "右下",
  },
  {
    seatId: 34,
    x: 600,
    y: 1278,
    width: 61,
    height: 61,
    shape: "square",
    area: "右下",
  },
  {
    seatId: 35,
    x: 823,
    y: 1193,
    width: 61,
    height: 61,
    shape: "square",
    area: "右下",
  },
  {
    seatId: 36,
    x: 748,
    y: 1268,
    width: 61,
    height: 61,
    shape: "square",
    area: "右下",
  },
  {
    seatId: 37,
    x: 672,
    y: 1343,
    width: 61,
    height: 61,
    shape: "square",
    area: "右下",
  },

  // 窓際 (37-41) → seatId 38-42
  {
    seatId: 38,
    x: 1050,
    y: 1028,
    width: 61,
    height: 81,
    shape: "tate",
    area: "窓際",
  },
  {
    seatId: 39,
    x: 1050,
    y: 1115,
    width: 61,
    height: 81,
    shape: "tate",
    area: "窓際",
  },
  {
    seatId: 40,
    x: 1050,
    y: 1202,
    width: 61,
    height: 81,
    shape: "tate",
    area: "窓際",
  },
  {
    seatId: 41,
    x: 1050,
    y: 1290,
    width: 61,
    height: 81,
    shape: "tate",
    area: "窓際",
  },
  {
    seatId: 42,
    x: 1050,
    y: 1378,
    width: 61,
    height: 81,
    shape: "tate",
    area: "窓際",
  },
];

// 座席位置データ（相対座標: %）
// 計算式: xPercent = (x / 1142) * 100, yPercent = ((y - 133) / 1549) * 100
export const SEAT_POSITIONS_RELATIVE: SeatPositionRelative[] =
  SEAT_POSITIONS.map(toRelativePosition);

// 座席位置をスケーリングする関数（レガシー互換）
export function scaleSeatPosition(
  seat: SeatPosition,
  scale: number
): SeatPosition {
  return {
    ...seat,
    x: seat.x * scale,
    y: seat.y * scale,
    width: seat.width * scale,
    height: seat.height * scale,
  };
}
